// Personal-wall media (spec 3b §2, §4.2): photos and video thumbnails; videos never play here.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight, Play } from "lucide-react";
import type { WallItem } from "../api/content";
import { formatDuration } from "./formatDuration";
import { isGalleryItem, isVideoItem } from "./registry";
import Img from "../components/ui/Img";

const MIN_RATIO = 0.8; // 4:5
const MAX_RATIO = 1.91; // 1.91:1

/** Frame ratio (width / height) from the first item, clamped to 4:5 … 1.91:1. */
export function frameRatio(first: WallItem | undefined): number {
  const w = first?.width ?? 0;
  const h = first?.height ?? 0;
  if (w <= 0 || h <= 0) return 1;
  return Math.min(MAX_RATIO, Math.max(MIN_RATIO, Math.round((w / h) * 100) / 100));
}

export const carouselItems = (items: WallItem[]) => items.filter(isGalleryItem);

interface Props {
  items: WallItem[];
  onOpen(index: number): void;
  /** Post dialog media column (Figma 2727:602): fills its box edge to edge instead of the card's framed ratio. */
  fill?: boolean;
  /** Called whenever the shown page changes (arrows, dots). */
  onPageChange?(index: number): void;
}

const ARROW = "absolute top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/45 text-white flex items-center justify-center";
const DOT = "w-1.5 h-1.5 rounded-full"; // theme-exempt: dots over the photo
const DOT_ON = "bg-white"; // theme-exempt: dots over the photo
const DOT_OFF = "bg-white/50"; // theme-exempt: dots over the photo

export default function MediaCarousel({ items, onOpen, fill = false, onPageChange }: Props) {
  const { t } = useTranslation();
  const [idx, setIdx] = useState(0);
  if (items.length === 0) return null;
  const at = Math.min(idx, items.length - 1);
  const it = items[at];
  const video = isVideoItem(it);
  const src = video ? it.thumbnailUrl : it.photoUrl;
  const dur = video ? formatDuration(it.durationMs) : null;
  const count = items.length;
  const go = (i: number) => {
    setIdx(i);
    onPageChange?.(i);
  };

  return (
    <div
      data-testid="media-carousel"
      data-fill={fill ? "true" : undefined}
      className={fill ? "relative w-full h-full overflow-hidden bg-[var(--bg-elevated)]" : "relative mx-4 overflow-hidden rounded-lg bg-[var(--bg-elevated)]"}
      style={fill ? undefined : { aspectRatio: String(frameRatio(items[0])) }}
    >
      <button type="button" aria-label={video ? t("wall_video_play") : t("wall_open_photo")} onClick={() => onOpen(at)} className="absolute inset-0 w-full h-full">
        {src && <Img src={src} alt="" className="w-full h-full object-cover" />}
        {video && (
          <span data-testid="video-play-badge" className="absolute inset-0 m-auto w-14 h-14 rounded-full bg-black/55 text-white flex items-center justify-center">
            <Play size={28} />
          </span>
        )}
        {dur && <span className="absolute right-2 bottom-2 rounded bg-black/65 px-1.5 py-0.5 text-xs text-white">{dur}</span>}
      </button>
      {count > 1 && (
        <>
          {at > 0 && (
            <button type="button" aria-label={t("wall_carousel_prev")} className={`${ARROW} left-2`} onClick={() => go(at - 1)}>
              <ChevronLeft size={16} />
            </button>
          )}
          {at < count - 1 && (
            <button type="button" aria-label={t("wall_carousel_next")} className={`${ARROW} right-2`} onClick={() => go(at + 1)}>
              <ChevronRight size={16} />
            </button>
          )}
          <span className="absolute right-2 top-2 rounded-full bg-black/55 px-2 py-0.5 text-xs text-white">
            {t("wall_carousel_page", { n: at + 1, total: count })}
          </span>
          <div className="absolute bottom-2 left-0 right-0 flex justify-center gap-1">
            {items.map((x, i) => (
              <button
                key={x.id}
                type="button"
                aria-label={t("wall_carousel_goto", { n: i + 1 })}
                aria-current={i === at}
                className={`${DOT} ${i === at ? DOT_ON : DOT_OFF}`}
                onClick={() => go(i)}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
